import {
    AutocompleteInput,
    DateInput,
    Edit,
    FileField,
    FileInput,
    NumberInput,
    ReferenceInput,
    SelectInput,
    SimpleForm,
    TextInput,
    BooleanInput
} from "@/components/admin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, Landmark, Truck, ArrowLeft } from "lucide-react";
import { required, useRecordContext } from "ra-core";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { DocumentItemsTable } from '../shared/DocumentItemsTable';
import { GrandTotalsWatcher } from '../shared/GrandTotalsWatcher';
import { RevisedReferenceWatcher } from '../shared/RevisedReferenceWatcher';
import { TermsTemplateSelector } from '../shared/TermsTemplateSelector';
import { WatermarkOverlay } from '../shared/WatermarkOverlay';
import { requireNonZeroTotal } from '../shared/validators';
import { FilteredContactInput } from '../../misc/FilteredContactInput';
import { SO_DOCUMENT_TYPE_CHOICES } from '../shared/companyConstants';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

// Local storage handler for saving handover details locally as of now i am doing with this once teh format is okey then we proceed with db bro 
const HandoverLocalStorageHandler = () => {
    const record = useRecordContext();
    const { setValue, watch } = useFormContext();

    useEffect(() => {
        if (!record || !record.id) return;
        const key = `gsw_so_handover_${record.id}`;
        const storedStr = localStorage.getItem(key);
        if (storedStr) {
            try {
                const data = JSON.parse(storedStr);
                if (data && data._handover) {
                    setValue('_handover', data._handover);
                }
            } catch (e) {
                console.error("Failed to parse handover data", e);
            }
        } else {
            setValue('_handover', {
                header: {
                    doc_no: 'RVS-SO-000',
                    date: record.date || new Date().toISOString().split('T')[0],
                    rev_no: '00'
                },
                commercial: {
                    currency: 'INR',
                    unit_price_approval: 'Approved',
                    payment_terms: record.payment_term_days ? `${record.payment_term_days} days` : '',
                    credit_terms: '',
                    taxes_duties: 'GST @ 18%',
                    incoterms_freight: '',
                    special_conditions: ''
                },
                technical: {
                    part_number: '',
                    part_description: '',
                    drawing_number: '',
                    drawing_revision: '',
                    specification_standard: '',
                    scope_of_supply: '',
                    scope_exclusions: '',
                    customer_critical_characteristics: '',
                    special_process_requirements: '',
                    docs_attached: {
                        approved_drawings: false,
                        technical_specifications: false,
                        bom_applicable: false,
                        customer_standards: false,
                        process_requirements: false
                    }
                },
                execution: {
                    total_quantity: record.items?.reduce((sum: number, item: any) => sum + (item.quantity || 0), 0)?.toString() || '',
                    batch_size: '',
                    production_lead_time: '',
                    delivery_commitment: '',
                    priority_level: 'Normal',
                    capacity_availability_confirmed_by: '',
                    tooling_fixture_availability: 'Available',
                    gauge_requirement: '',
                    special_equipment_requirement: '',
                    delivery_schedule: [
                        { qty: '', delivery_date: '', remarks: '' },
                        { qty: '', delivery_date: '', remarks: '' },
                        { qty: '', delivery_date: '', remarks: '' },
                        { qty: '', delivery_date: '', remarks: '' }
                    ]
                },
                procurement: {
                    raw_material_specification: '',
                    approved_source_make: '',
                    long_lead_items: 'No',
                    procurement_lead_time: '',
                    customer_free_issue_material: 'No',
                    free_issue_material_details: ''
                },
                quality: {
                    docs_required: {
                        mtc: false,
                        dimensional_inspection: false,
                        cmm: false,
                        fai: false,
                        ppap: false,
                        salt_spray: false,
                        coating_inspection: false,
                        certificate_conformance: false,
                        packing_list: false,
                        dispatch_documents: false,
                        other: false,
                        other_details: ''
                    },
                    acceptance_criteria: ''
                },
                logistics: {
                    packaging_specification: '',
                    labelling_requirement: '',
                    dispatch_location: '',
                    consignee_details: record.shipping_address || '',
                    transportation_responsibility: 'Company',
                    mode_of_transport: 'Road',
                    special_handling_instructions: ''
                },
                risks: [
                    { description: '', owner: '', action: '' },
                    { description: '', owner: '', action: '' },
                    { description: '', owner: '', action: '' }
                ],
                customer_deviations: '',
                confirmation: {
                    sales: { name: '', signed: false, date: '' },
                    operations: { name: '', signed: false, date: '' },
                    planning: { name: '', signed: false, date: '' },
                    procurement: { name: '', signed: false, date: '' },
                    quality: { name: '', signed: false, date: '' },
                    logistics: { name: '', signed: false, date: '' },
                    finance: { name: '', signed: false, date: '' }
                },
                release_auth: {
                    sales: { name: '', designation: 'Sales', signed: false, date: '' },
                    operations: { name: '', designation: 'Operations', signed: false, date: '' }
                }
            });
        }
    }, [record, setValue]);

    const handoverData = watch('_handover');

    useEffect(() => {
        if (!record || !record.id) return;
        const key = `gsw_so_handover_${record.id}`;
        localStorage.setItem(key, JSON.stringify({
            _handover: handoverData
        }));
    }, [record, handoverData]);

    return null;
};

// Handover sections editing UI
const HandoverSections = () => {
    const { setValue } = useFormContext();

    return (
        <div className="space-y-6 pt-6 border-t border-slate-200">
            <div className="bg-[#00a68d]/5 p-4 rounded-lg border border-[#00a68d]/10 flex flex-col gap-1">
                <h3 className="text-[#00a68d] font-bold text-base">Sales Order Release & Project Handover Form</h3>
                <p className="text-xs text-slate-500">All data entered below is stored locally in your browser's Cache/LocalStorage and will be displayed in the print view of this Sales Order.</p>
            </div>

            <Card className="w-full shadow-sm">
                <CardHeader className="pb-3 border-b bg-slate-50/50">
                    <CardTitle className="text-lg font-semibold tracking-tight text-primary">Handover Form Header Details</CardTitle>
                </CardHeader>
                <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                    <TextInput source="_handover.header.doc_no" label="Doc No." defaultValue="RVS-SO-000" className="w-full" />
                    <DateInput source="_handover.header.date" label="Date" className="w-full" />
                    <TextInput source="_handover.header.rev_no" label="Rev No." defaultValue="00" className="w-full" />
                </CardContent>
            </Card>

            <Card className="w-full shadow-sm">
                <CardHeader className="pb-3 border-b bg-slate-50/50">
                    <CardTitle className="text-lg font-semibold tracking-tight text-primary">B. Commercial Details</CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <TextInput source="_handover.commercial.currency" label="Currency" defaultValue="INR" className="w-full" />
                        <SelectInput source="_handover.commercial.unit_price_approval" label="Unit Price Approval" choices={[
                            { id: 'Approved', name: 'Approved' },
                            { id: 'Exception Approved', name: 'Exception Approved' }
                        ]} className="w-full" />
                        <TextInput source="_handover.commercial.payment_terms" label="Payment Terms" className="w-full" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <TextInput source="_handover.commercial.credit_terms" label="Credit Terms" className="w-full" />
                        <TextInput source="_handover.commercial.taxes_duties" label="Taxes & Duties" defaultValue="GST @ 18%" className="w-full" />
                        <TextInput source="_handover.commercial.incoterms_freight" label="Incoterms / Freight Terms" className="w-full" />
                    </div>
                    <TextInput source="_handover.commercial.special_conditions" label="Special Commercial Conditions" multiline rows={2} className="w-full" />
                </CardContent>
            </Card>

            <Card className="w-full shadow-sm">
                <CardHeader className="pb-3 border-b bg-slate-50/50">
                    <CardTitle className="text-lg font-semibold tracking-tight text-primary">C. Product & Technical Information</CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <TextInput source="_handover.technical.part_number" label="Part Number" className="w-full" />
                        <TextInput source="_handover.technical.part_description" label="Part Description" className="w-full" />
                        <TextInput source="_handover.technical.drawing_number" label="Drawing Number" className="w-full" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <TextInput source="_handover.technical.drawing_revision" label="Drawing Revision" className="w-full" />
                        <TextInput source="_handover.technical.specification_standard" label="Specification / Standard" className="w-full" />
                        <TextInput source="_handover.technical.scope_of_supply" label="Scope of Supply" className="w-full" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <TextInput source="_handover.technical.scope_exclusions" label="Scope Exclusions" className="w-full" />
                        <TextInput source="_handover.technical.customer_critical_characteristics" label="Customer Critical Characteristics" className="w-full" />
                        <TextInput source="_handover.technical.special_process_requirements" label="Special Process Requirements" className="w-full" />
                    </div>
                    <div className="pt-4 border-t">
                        <h4 className="text-sm font-semibold mb-3 text-slate-700">Documents Attached</h4>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            <BooleanInput source="_handover.technical.docs_attached.approved_drawings" label="Approved Drawings" />
                            <BooleanInput source="_handover.technical.docs_attached.technical_specifications" label="Technical Specs" />
                            <BooleanInput source="_handover.technical.docs_attached.bom_applicable" label="BOM (if applicable)" />
                            <BooleanInput source="_handover.technical.docs_attached.customer_standards" label="Customer Standards" />
                            <BooleanInput source="_handover.technical.docs_attached.process_requirements" label="Process Requirements" />
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card className="w-full shadow-sm">
                <CardHeader className="pb-3 border-b bg-slate-50/50">
                    <CardTitle className="text-lg font-semibold tracking-tight text-primary">D & E. Order Execution Plan & Delivery Schedule</CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <TextInput source="_handover.execution.total_quantity" label="Total Order Quantity" className="w-full" />
                        <TextInput source="_handover.execution.batch_size" label="Batch / Lot Size" className="w-full" />
                        <TextInput source="_handover.execution.production_lead_time" label="Production Lead Time" className="w-full" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <TextInput source="_handover.execution.delivery_commitment" label="Delivery Commitment" className="w-full" />
                        <SelectInput source="_handover.execution.priority_level" label="Priority Level" choices={[
                            { id: 'Normal', name: 'Normal' },
                            { id: 'Urgent', name: 'Urgent' },
                            { id: 'Critical', name: 'Critical' }
                        ]} className="w-full" />
                        <TextInput source="_handover.execution.capacity_availability_confirmed_by" label="Capacity Availability Confirmed By" className="w-full" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <SelectInput source="_handover.execution.tooling_fixture_availability" label="Tooling / Fixture Availability" choices={[
                            { id: 'Available', name: 'Available' },
                            { id: 'To be Arranged', name: 'To be Arranged' }
                        ]} className="w-full" />
                        <TextInput source="_handover.execution.gauge_requirement" label="Gauge Requirement" className="w-full" />
                        <TextInput source="_handover.execution.special_equipment_requirement" label="Special Equipment Requirement" className="w-full" />
                    </div>
                    <div className="pt-4 border-t">
                        <h4 className="text-sm font-semibold mb-3 text-slate-700">E. Delivery Schedule</h4>
                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                                <thead>
                                    <tr className="border-b text-xs text-slate-500 font-medium text-left">
                                        <th className="pb-2 w-12 text-center">Sr. No.</th>
                                        <th className="pb-2 px-2">Quantity</th>
                                        <th className="pb-2 px-2">Required Delivery Date</th>
                                        <th className="pb-2 px-2">Remarks</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[0, 1, 2, 3].map((idx) => (
                                        <tr key={idx} className="border-b border-slate-100 last:border-0">
                                            <td className="py-2 text-center text-sm font-medium text-slate-500">{idx + 1}</td>
                                            <td className="py-2 px-2">
                                                <TextInput source={`_handover.execution.delivery_schedule.${idx}.qty`} label={false} className="w-full" helperText={false} />
                                            </td>
                                            <td className="py-2 px-2">
                                                <DateInput source={`_handover.execution.delivery_schedule.${idx}.delivery_date`} label={false} className="w-full" helperText={false} />
                                            </td>
                                            <td className="py-2 px-2">
                                                <TextInput source={`_handover.execution.delivery_schedule.${idx}.remarks`} label={false} className="w-full" helperText={false} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card className="w-full shadow-sm">
                <CardHeader className="pb-3 border-b bg-slate-50/50">
                    <CardTitle className="text-lg font-semibold tracking-tight text-primary">F. Procurement Requirements</CardTitle>
                </CardHeader>
                <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                    <TextInput source="_handover.procurement.raw_material_specification" label="Raw Material Specification" className="w-full" />
                    <TextInput source="_handover.procurement.approved_source_make" label="Approved Source / Make" className="w-full" />
                    <SelectInput source="_handover.procurement.long_lead_items" label="Long Lead Items" choices={[
                        { id: 'Yes', name: 'Yes' },
                        { id: 'No', name: 'No' }
                    ]} className="w-full" />
                    <TextInput source="_handover.procurement.procurement_lead_time" label="Procurement Lead Time" className="w-full" />
                    <SelectInput source="_handover.procurement.customer_free_issue_material" label="Customer Free-Issue Material" choices={[
                        { id: 'Yes', name: 'Yes' },
                        { id: 'No', name: 'No' }
                    ]} className="w-full" />
                    <TextInput source="_handover.procurement.free_issue_material_details" label="Free-Issue Material Details" className="w-full" />
                </CardContent>
            </Card>

            <Card className="w-full shadow-sm">
                <CardHeader className="pb-3 border-b bg-slate-50/50">
                    <CardTitle className="text-lg font-semibold tracking-tight text-primary">G. Quality Requirements</CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-6">
                    <div>
                        <h4 className="text-sm font-semibold mb-3 text-slate-700">Customer Documentation Requirements</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                            <BooleanInput source="_handover.quality.docs_required.mtc" label="Material Test Certificate (MTC)" />
                            <BooleanInput source="_handover.quality.docs_required.dimensional_inspection" label="Dimensional Inspection Report" />
                            <BooleanInput source="_handover.quality.docs_required.cmm" label="CMM Report" />
                            <BooleanInput source="_handover.quality.docs_required.fai" label="First Article Inspection (FAI)" />
                            <BooleanInput source="_handover.quality.docs_required.ppap" label="PPAP Submission" />
                            <BooleanInput source="_handover.quality.docs_required.salt_spray" label="Salt Spray Test Report" />
                            <BooleanInput source="_handover.quality.docs_required.coating_inspection" label="Coating Inspection Report" />
                            <BooleanInput source="_handover.quality.docs_required.certificate_conformance" label="Certificate of Conformance" />
                            <BooleanInput source="_handover.quality.docs_required.packing_list" label="Packing List" />
                            <BooleanInput source="_handover.quality.docs_required.dispatch_documents" label="Dispatch Documents" />
                            <BooleanInput source="_handover.quality.docs_required.other" label="Other Requirements" />
                        </div>
                        <div className="mt-4">
                            <TextInput source="_handover.quality.docs_required.other_details" label="Other Requirements Details" className="w-full" />
                        </div>
                    </div>
                    <div className="pt-4 border-t">
                        <TextInput source="_handover.quality.acceptance_criteria" label="Quality Requirements / Acceptance Criteria" multiline rows={4} className="w-full" />
                    </div>
                </CardContent>
            </Card>

            <Card className="w-full shadow-sm">
                <CardHeader className="pb-3 border-b bg-slate-50/50">
                    <CardTitle className="text-lg font-semibold tracking-tight text-primary">H. Packaging & Logistics Requirements</CardTitle>
                </CardHeader>
                <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                    <TextInput source="_handover.logistics.packaging_specification" label="Packaging Specification" className="w-full" />
                    <TextInput source="_handover.logistics.labelling_requirement" label="Labelling Requirement" className="w-full" />
                    <TextInput source="_handover.logistics.dispatch_location" label="Dispatch Location" className="w-full" />
                    <TextInput source="_handover.logistics.consignee_details" label="Consignee Details" multiline rows={2} className="w-full col-span-1 md:col-span-2" />
                    <SelectInput source="_handover.logistics.transportation_responsibility" label="Transportation Responsibility" choices={[
                        { id: 'Customer', name: 'Customer' },
                        { id: 'Company', name: 'Company' },
                        { id: 'Third Party', name: 'Third Party' }
                    ]} className="w-full" />
                    <SelectInput source="_handover.logistics.mode_of_transport" label="Mode of Transport" choices={[
                        { id: 'Road', name: 'Road' },
                        { id: 'Air', name: 'Air' },
                        { id: 'Sea', name: 'Sea' },
                        { id: 'Courier', name: 'Courier' }
                    ]} className="w-full" />
                    <TextInput source="_handover.logistics.special_handling_instructions" label="Special Handling Instructions" multiline rows={2} className="w-full col-span-1 md:col-span-3" />
                </CardContent>
            </Card>

            <Card className="w-full shadow-sm">
                <CardHeader className="pb-3 border-b bg-slate-50/50">
                    <CardTitle className="text-lg font-semibold tracking-tight text-primary">I & J. Risks, Assumptions, Dependencies & Customer Deviations</CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-6">
                    <div>
                        <h4 className="text-sm font-semibold mb-3 text-slate-700">I. Risks, Assumptions & Dependencies</h4>
                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                                <thead>
                                    <tr className="border-b text-xs text-slate-500 font-medium text-left">
                                        <th className="pb-2 px-2">Description</th>
                                        <th className="pb-2 px-2 w-1/4">Owner</th>
                                        <th className="pb-2 px-2 w-1/3">Action Required</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[0, 1, 2].map((idx) => (
                                        <tr key={idx} className="border-b border-slate-100 last:border-0">
                                            <td className="py-2 px-2">
                                                <TextInput source={`_handover.risks.${idx}.description`} label={false} className="w-full" helperText={false} />
                                            </td>
                                            <td className="py-2 px-2">
                                                <TextInput source={`_handover.risks.${idx}.owner`} label={false} className="w-full" helperText={false} />
                                            </td>
                                            <td className="py-2 px-2">
                                                <TextInput source={`_handover.risks.${idx}.action`} label={false} className="w-full" helperText={false} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="pt-4 border-t">
                        <TextInput source="_handover.customer_deviations" label="J. Customer Deviations / Special Approvals" multiline rows={4} className="w-full" />
                    </div>
                </CardContent>
            </Card>

            <Card className="w-full shadow-sm">
                <CardHeader className="pb-3 border-b bg-slate-50/50">
                    <CardTitle className="text-lg font-semibold tracking-tight text-primary">K. Cross-Functional Handover & Confirmation</CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                    <p className="text-xs text-slate-500 italic mb-4">
                        "The undersigned confirm that the information provided above has been reviewed and is adequate for execution of the order in line with customer requirements."
                    </p>
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="border-b text-xs text-slate-500 font-medium text-left">
                                    <th className="pb-2 px-2 w-1/4">Department</th>
                                    <th className="pb-2 px-2 w-1/3">Name</th>
                                    <th className="pb-2 px-2 text-center w-24">Signed</th>
                                    <th className="pb-2 px-2 w-1/4">Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {['sales', 'operations', 'planning', 'procurement', 'quality', 'logistics', 'finance'].map((dept) => (
                                    <tr key={dept} className="border-b border-slate-100 last:border-0">
                                        <td className="py-3 px-2 font-medium text-sm capitalize text-slate-700">
                                            {dept === 'finance' ? 'Finance (if applicable)' : dept}
                                        </td>
                                        <td className="py-2 px-2">
                                            <TextInput source={`_handover.confirmation.${dept}.name`} label={false} className="w-full" helperText={false} />
                                        </td>
                                        <td className="py-2 px-2 text-center">
                                            <BooleanInput source={`_handover.confirmation.${dept}.signed`} label={false} className="inline-block" />
                                        </td>
                                        <td className="py-2 px-2">
                                            <DateInput source={`_handover.confirmation.${dept}.date`} label={false} className="w-full" helperText={false} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>

            <Card className="w-full shadow-sm">
                <CardHeader className="pb-3 border-b bg-slate-50/50">
                    <CardTitle className="text-lg font-semibold tracking-tight text-primary">Release Authorization</CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="space-y-4 p-5 border rounded-lg bg-slate-50/50 shadow-sm">
                            <h4 className="font-semibold text-sm text-slate-800 border-b pb-2">Released By (Sales)</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <TextInput source="_handover.release_auth.sales.name" label="Name" className="w-full" helperText={false} />
                                <TextInput source="_handover.release_auth.sales.designation" label="Designation" defaultValue="Sales" className="w-full" helperText={false} />
                                <div className="col-span-1 md:col-span-2 pt-2 border-t border-slate-200/50 flex items-center justify-between">
                                    <BooleanInput source="_handover.release_auth.sales.signed" label="Signature Approved" />
                                    <DateInput source="_handover.release_auth.sales.date" label="Date" className="w-40" helperText={false} />
                                </div>
                            </div>
                        </div>
                        <div className="space-y-4 p-5 border rounded-lg bg-slate-50/50 shadow-sm">
                            <h4 className="font-semibold text-sm text-slate-800 border-b pb-2">Accepted for Execution (Operations)</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <TextInput source="_handover.release_auth.operations.name" label="Name" className="w-full" helperText={false} />
                                <TextInput source="_handover.release_auth.operations.designation" label="Designation" defaultValue="Operations" className="w-full" helperText={false} />
                                <div className="col-span-1 md:col-span-2 pt-2 border-t border-slate-200/50 flex items-center justify-between">
                                    <BooleanInput source="_handover.release_auth.operations.signed" label="Signature Approved" />
                                    <DateInput source="_handover.release_auth.operations.date" label="Date" className="w-40" helperText={false} />
                                </div>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

const SalesOrderEditTitle = () => {
    const record = useRecordContext();
    if (!record) return null;
    return (
        <div className="flex items-center gap-3">
            <Button asChild variant="outline" size="sm" className="gap-1.5 bg-[#00a68d]/10 hover:bg-[#00a68d]/20 text-[#00a68d] hover:text-[#00947e] border border-[#00a68d]/20 hover:border-[#00a68d]/30 font-semibold cursor-pointer h-9 px-4 rounded-lg">
                <Link to="/sales_orders" className="flex items-center gap-1.5">
                    <ArrowLeft className="w-4 h-4 text-[#00a68d]" /> Back
                </Link>
            </Button>
            <span className="text-xl font-bold tracking-tight">Edit Sales Order {record.reference}</span>
        </div>
    );
};

export const SalesOrderEdit = () => (
    <Edit title={<SalesOrderEditTitle />}>
        <SimpleForm className="max-w-5xl mx-auto space-y-6 relative" validate={requireNonZeroTotal}>
            <WatermarkOverlay />
            <Card className="w-full shadow-sm">
                <CardHeader className="pb-3 border-b bg-slate-50/50">
                    <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-primary" />
                        <CardTitle className="text-lg font-semibold tracking-tight">Sales Order Details</CardTitle>
                    </div>
                </CardHeader>
                <CardContent className="pt-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <TextInput source="reference" label="Sales Order No." disabled className="w-full opacity-70" helperText={false} />
                        <DateInput source="date" label="SO Date" validate={required()} className="w-full" />
                        <DateInput source="valid_until" label="Valid Until" className="w-full" />
                        <SelectInput
                            source="status"
                            className="w-full"
                            choices={[
                                { id: 'draft', name: 'Draft' },
                                { id: 'sent', name: 'Sent' },
                                { id: 'approved', name: 'Approved' },
                                { id: 'rejected', name: 'Rejected' },
                                { id: 'revised', name: 'Revised' },
                            ]}
                        />
                    </div>
                    {/* SO-1 / SO-2: document type + customer PO ref + payment terms */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-6">
                        <SelectInput
                            source="document_type"
                            label="Document Type"
                            choices={SO_DOCUMENT_TYPE_CHOICES}
                            defaultValue="Tax Invoice"
                            className="w-full"
                        />
                        <TextInput source="customer_po_no" label="Customer PO No." className="w-full" />
                        <DateInput source="customer_po_date" label="Customer PO Date" className="w-full" />
                        <TextInput source="payment_term_days" label="Payment Terms (days)" className="w-full" helperText="e.g. 30" />
                    </div>
                </CardContent>
            </Card>

            <Card className="w-full shadow-sm">
                <CardHeader className="pb-3 border-b bg-slate-50/50">
                    <div className="flex items-center gap-2">
                        <Landmark className="w-5 h-5 text-primary" />
                        <CardTitle className="text-lg font-semibold tracking-tight">Customer & Project</CardTitle>
                    </div>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <ReferenceInput source="company_id" reference="companies">
                            <AutocompleteInput label="Select Customer" validate={required()} className="w-full" />
                        </ReferenceInput>
                        <FilteredContactInput label="Contact Person" className="w-full" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                        <ReferenceInput source="project_id" reference="projects">
                            <AutocompleteInput label="Link to Project" className="w-full" />
                        </ReferenceInput>

                        <ReferenceInput source="quotation_id" reference="quotations">
                            <AutocompleteInput label="From Quotation" optionText="reference" className="w-full" />
                        </ReferenceInput>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                        <TextInput source="billing_address" label="Billing Address" multiline rows={3} className="w-full" />
                        <TextInput source="shipping_address" label="Shipping / Site Address" multiline rows={3} className="w-full" />
                    </div>
                </CardContent>
            </Card>

            <Card className="w-full shadow-sm">
                <CardHeader className="pb-3 border-b bg-slate-50/50">
                    <div className="flex items-center gap-2">
                        <Truck className="w-5 h-5 text-primary" />
                        <CardTitle className="text-lg font-semibold tracking-tight">Line Items & Financials</CardTitle>
                    </div>
                </CardHeader>
                <CardContent className="pt-6 space-y-6">
                    <DocumentItemsTable />
                    <GrandTotalsWatcher />
                    <RevisedReferenceWatcher />

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 bg-slate-50 p-4 rounded-lg border">
                        <NumberInput source="cgst_amount" label="CGST (₹)" readOnly className="w-full" />
                        <NumberInput source="sgst_amount" label="SGST (₹)" readOnly className="w-full" />
                        <NumberInput source="igst_amount" label="IGST (₹)" readOnly className="w-full" />
                        <NumberInput source="total_amount" label="Total Amount (₹)" readOnly className="w-full font-bold" />
                    </div>

                    <TextInput source="amount_in_words" label="Amount in Words" readOnly className="w-full mt-2" />

                    {/* File Attachment / Drawing Upload */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                        <TextInput source="file_url" label="Drawing / Document File URL" placeholder="https://example.com/drawing.pdf" className="w-full" helperText="Paste a drive link, or upload a file using the field next to this." />
                        <FileInput source="file_url_upload" label="Upload File (Overrides URL)" accept={{ "application/pdf": [".pdf"], "image/*": [".png", ".jpg", ".jpeg"], "application/msword": [".doc"], "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"], "application/vnd.ms-excel": [".xls"], "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"], "application/zip": [".zip"] }} className="w-full">
                            <FileField source="src" title="title" />
                        </FileInput>
                    </div>
                </CardContent>
            </Card>

            <Card className="w-full shadow-sm">
                <CardHeader className="pb-3 border-b bg-slate-50/50">
                    <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-primary" />
                            <CardTitle className="text-lg font-semibold tracking-tight">Terms & Notes</CardTitle>
                        </div>
                        <TermsTemplateSelector type="quotation" />
                    </div>
                </CardHeader>
                <CardContent className="pt-6">
                    <div className="space-y-6">
                        <TextInput source="terms_conditions" label="Terms & Conditions" multiline rows={4} className="w-full" />
                        <TextInput source="comments" label="Internal Comments / Notes" multiline rows={3} className="w-full" />
                    </div>
                </CardContent>
            </Card>

            <HandoverLocalStorageHandler />
            <HandoverSections />
        </SimpleForm>
    </Edit>
);
